import type { ComputedRef, Ref } from "vue";
import type { LoopbackFilterLogicalOperatorEnum } from "../Api/Type/LoopbackFilter/LoopbackFilterLogicalOperator.enum";
import type { LoopbackFilterOperatorEnum } from "../Api/Type/LoopbackFilter/LoopbackFilterOperator.enum";
import { refDebounced } from "@vueuse/core";
import { computed, inject, provide, ref } from "vue";

const SearchableKey = Symbol("Searchable");

export interface SearchableField<T> {
  name: T
  logicalOperator: LoopbackFilterLogicalOperatorEnum
  operator: LoopbackFilterOperatorEnum
  index?: number
}

export function createSearchable<T>(
  searchableFields: Array<Extract<keyof T, string> | SearchableField<Extract<keyof T, string>>>,
  items?: Ref<T[]> | ComputedRef<T[]> | undefined,
  searchFieldKey: string = "name",
  paramOverride?: (filter: string) => string,
) {
  const searchValue = ref<string | undefined>("");
  const searchDisabled = ref(false);
  // const { debounce, debouncedValue } = useDebounce();
  const debouncedValue = refDebounced(searchValue, 300);

  // watch(searchValue, () => debounce(searchValue.value));

  function clearSearch() {
    searchValue.value = "";
  }

  function enableSearch() {
    searchDisabled.value = false;
  }

  function disableSearch() {
    searchDisabled.value = true;
  }

  const searchEnabled = computed(() => {
    return (
      searchValue.value?.trim() !== ""
      && searchDisabled.value === false
    );
  });

  function setSearchUrlParams(urlParams: URLSearchParams) {
    if (searchEnabled.value && debouncedValue.value) {
      if (searchableFields.length === 1) {
        urlParams.append(convertFilterToUrlParam(searchableFields[0], 0), debouncedValue.value);
      } else {
        searchableFields.forEach((filter, index) => {
          if (debouncedValue.value) {
            urlParams.append(convertFilterToUrlParam(filter, index), debouncedValue.value);
          }
        });
      }
    }
  }

  function convertFilterToUrlParam(filter: SearchableField<Extract<keyof T, string>> | string, index: number) {
    if (typeof filter === "string") {
      if (paramOverride) {
        return paramOverride(filter);
      }
      return `filter[where][${filter}][ilike]`;
    }
    if (paramOverride) {
      return paramOverride(filter.name);
    }
    return `filter[where][${filter.logicalOperator}][${filter?.index || index}][${filter.name}][${filter.operator}]`;
  }

  const filtered = computed(() => {
    if (!items) {
      return [];
    }
    if (searchDisabled.value) {
      return items.value;
    }

    if (searchValue.value?.trim() === "") {
      return items.value;
    }

    return items.value.filter((item: any) => {
      if (!(searchFieldKey in item)) {
        return false;
      }
      if (typeof item[searchFieldKey] !== "string") {
        return false;
      }
      return item[searchFieldKey].match(new RegExp(searchValue.value || "", "gi"));
    });
  });

  function highlight(word: string | null) {
    if (!word) {
      return word;
    }
    if (searchValue.value?.trim() !== "") {
      return word;
    }
    const check = new RegExp(searchValue.value || "", "gi");
    return word
      .toString()
      .replace(check, matchedText => `<mark>${matchedText}</mark>`);
  }

  return {
    clearSearch,
    setSearchUrlParams,
    searchValue,
    debouncedValue,
    searchableFields,
    searchEnabled,
    searchDisabled,
    highlight,
    filtered,
    enableSearch,
    disableSearch,
  };
}

type CreateSearchableParameters<T> = Parameters<typeof createSearchable<T>>;
export function provideSearchable<T>(...config: CreateSearchableParameters<T>) {
  const instance = createSearchable<T>(...config);
  provide(SearchableKey, instance);
  return instance;
}

/**
 * A searchable of any item type. `ReturnType<typeof createSearchable>` resolves `T` to `unknown`, whose search fields
 * are `never`, so a typed instance (`createSearchable<UserHydraItem>(["name"])`) would not fit; a prop that accepts the
 * searchable of any list uses this type instead.
 */
export type Searchable<T = any> = ReturnType<typeof createSearchable<T>>;

type UseSearchable = ReturnType<typeof createSearchable>;
export function useSearchable() {
  const instance = inject<UseSearchable>(SearchableKey);

  if (!instance) {
    throw new Error("Run provideSearchable before useSearchable.");
  }

  return instance;
}

export function useOptionalSearchable(): UseSearchable | null {
  return inject<UseSearchable | null>(SearchableKey, null);
}
